import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { X, Trash2, Settings, Search } from "lucide-react";
import { useMutation } from "@apollo/client";
import type { PageForEdit } from "../pages/types";
import { UPDATE_PAGE, DELETE_PAGE, evictPageListings } from "../pages/mutations";
import { ImageUploadField } from "./inspector/ImageUploadField";
import { useToast } from "../../../components/ui/Toast";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { SeoPanel } from "./SeoPanel";

type Tab = "general" | "seo";

interface PageSettingsProps {
  page: PageForEdit;
  open: boolean;
  onClose: () => void;
}

export function PageSettings({ page, open, onClose }: PageSettingsProps) {
  const toast = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>("general");

  const [title, setTitle] = useState(page.title ?? "");
  const [description, setDescription] = useState(page.description ?? "");
  const [ogImageUrl, setOgImageUrl] = useState(page.ogImageUrl ?? "");
  const [showInNav, setShowInNav] = useState(page.showInNav ?? true);
  const [slug, setSlug] = useState(page.slug ?? "");
  const [attribution, setAttribution] = useState(page.attribution ?? "");

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [updatePage, { loading }] = useMutation(UPDATE_PAGE, {
    update: evictPageListings,
  });

  const [deletePage, { loading: isDeleting }] = useMutation(DELETE_PAGE, {
    update: evictPageListings,
  });

  const handleDelete = useCallback(async () => {
    try {
      await deletePage({ variables: { id: page.id } });
      toast.push({
        kind: "success",
        title: `"${page.title || page.slug}" deleted`,
      });
      navigate("/website/pages");
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Couldn't delete page",
        description: err instanceof Error ? err.message : "Unknown error",
      });
      setShowDeleteConfirm(false);
    }
  }, [deletePage, page.id, page.title, page.slug, toast, navigate]);

  const handleSave = useCallback(async () => {
    try {
      await updatePage({
        variables: {
          id: page.id,
          title: title || null,
          description: description || null,
          ogImageUrl: ogImageUrl || null,
          showInNav,
          attribution: attribution || null,
          ...(page.isHome ? {} : { slug: slug || undefined }),
        },
      });
      toast.push({ kind: "success", title: "Page settings saved" });
      onClose();
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Couldn't save settings",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }, [
    updatePage,
    page.id,
    page.isHome,
    title,
    description,
    ogImageUrl,
    showInNav,
    slug,
    attribution,
    toast,
    onClose,
  ]);

  return (
    <aside
      className="flex-none overflow-hidden bg-paper-hi transition-[width] duration-[220ms] ease-[cubic-bezier(0.3,0.9,0.3,1)]"
      style={{
        width: open ? 320 : 0,
        borderLeft: open
          ? "1px solid var(--color-ink-6)"
          : "1px solid transparent",
      }}
    >
      <div className="w-[320px] h-full overflow-y-auto px-[18px] pt-[18px] pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-tide">
            Page settings
          </span>
          <button
            onClick={onClose}
            title="Close"
            className="text-ink-5 p-1 rounded-[7px] hover:bg-paper-2 transition-colors"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-0 mb-5 border-b border-ink-6">
          <TabButton
            active={activeTab === "general"}
            onClick={() => setActiveTab("general")}
            icon={<Settings size={13} />}
            label="General"
          />
          <TabButton
            active={activeTab === "seo"}
            onClick={() => setActiveTab("seo")}
            icon={<Search size={13} />}
            label="SEO"
          />
        </div>

        {activeTab === "general" && (
          <>
            {/* Title */}
            <div className="mb-[18px]">
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Page title"
                className="w-full px-3 py-2 text-[13px] rounded-[10px] border border-ink-6 bg-paper text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide transition-colors"
              />
              <p className="mt-1 text-[10.5px] text-ink-5">
                Used as the SEO title tag
              </p>
            </div>

            {/* Description */}
            <div className="mb-[18px]">
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Page description for search engines"
                rows={3}
                className="w-full px-3 py-2 text-[13px] rounded-[10px] border border-ink-6 bg-paper text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide transition-colors resize-none"
              />
              <p className="mt-1 text-[10.5px] text-ink-5">
                Meta description for SEO
              </p>
            </div>

            {/* Link preview image */}
            <ImageUploadField
              label="Link preview image"
              value={ogImageUrl || undefined}
              onChange={(url) => setOgImageUrl(url)}
            />

            {/* Show in navigation */}
            <div className="mb-[18px]">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInNav}
                  onChange={(e) => setShowInNav(e.target.checked)}
                  className="w-4 h-4 rounded border-ink-6 text-tide focus:ring-tide accent-tide"
                />
                <span className="text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4">
                  Show in navigation
                </span>
              </label>
              <p className="mt-1 text-[10.5px] text-ink-5 ml-[26px]">
                When enabled, this page can be added to the site navigation.
              </p>
            </div>

            {/* Attribution */}
            <div className="mb-[18px]">
              <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                Attribution
              </label>
              <input
                type="text"
                value={attribution}
                onChange={(e) => setAttribution(e.target.value)}
                placeholder="e.g. google-ads-q3"
                className="w-full px-3 py-2 text-[13px] rounded-[10px] border border-ink-6 bg-paper text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide transition-colors"
              />
              <p className="mt-1 text-[10.5px] text-ink-5">
                Tags bookings from this page for lead source tracking.
                Appended as <code className="font-mono text-[10px]">?client=</code> to booking links.
              </p>
            </div>

            {/* URL path (hidden for home page per D9) */}
            {!page.isHome && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  URL path
                </label>
                <div className="flex items-center gap-0">
                  <span className="text-[12px] text-ink-5 font-mono px-2 py-2 bg-paper-2 border border-ink-6 border-r-0 rounded-l-[10px]">
                    /
                  </span>
                  <input
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="page-url"
                    className="flex-1 min-w-0 px-3 py-2 text-[13px] font-mono rounded-r-[10px] border border-ink-6 bg-paper text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Save */}
            <button
              onClick={handleSave}
              disabled={loading}
              className="w-full text-[13px] font-semibold rounded-[10px] px-3.5 py-2.5 border border-tide bg-tide text-white hover:bg-tide-d disabled:opacity-45 disabled:cursor-default transition-colors mt-2"
            >
              {loading ? "Saving..." : "Save settings"}
            </button>

            {/* Danger zone */}
            <div className="mt-10 pt-5 border-t border-ink-6">
              <span className="block text-[10.5px] font-bold tracking-[0.09em] uppercase text-rust mb-3">
                Danger zone
              </span>
              {page.isHome ? (
                <p className="text-[12px] text-ink-4 leading-relaxed">
                  The home page cannot be deleted. To remove it, first set another
                  page as the home page.
                </p>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  className="flex items-center gap-2 w-full text-[13px] font-semibold rounded-[10px] px-3.5 py-2.5 border border-rust/30 text-rust hover:bg-rust/5 disabled:opacity-45 disabled:cursor-default transition-colors"
                >
                  <Trash2 size={14} strokeWidth={2} />
                  {isDeleting ? "Deleting..." : "Delete this page"}
                </button>
              )}
            </div>
          </>
        )}

        {activeTab === "seo" && <SeoPanel page={page} />}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        variant="danger"
        title={<>Delete &ldquo;{page.title || page.slug}&rdquo;?</>}
        description="This permanently removes the page and all its content. This action cannot be undone."
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirming={isDeleting}
      />
    </aside>
  );
}

// ── Tab button sub-component ──────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold border-b-2 transition-colors ${
        active
          ? "border-tide text-tide"
          : "border-transparent text-ink-4 hover:text-ink-3"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
